import { useCallback, useEffect, useRef, useState } from "react";
import { mailsService } from "../../services/mails.service";
import {
  getAttachmentKind,
  isAttachmentAvailable,
  isInboundAttachment,
  UNAVAILABLE_REASON_LABELS,
} from "../../utils/attachmentKinds";
import {
  formatFileSize,
  getAttachmentDisplayName,
  getAttachmentSizeInBytes,
} from "../../utils/fileUtils";
import styles from "./AttachmentList.module.css";
import { useModalFocusTrap } from "./useModalFocusTrap";
import ModalOverlay from "../../ui/Modals/ModalOverlay";

const getAttachmentKey = (attachment, index) =>
  `${attachment.source || "account"}-${attachment.id ?? index}`;

const getSizeLabel = (attachment) => {
  const bytes = getAttachmentSizeInBytes(attachment);
  return bytes ? formatFileSize(bytes) : "";
};

const fetchFreshUrl = async (attachment) =>
  isInboundAttachment(attachment)
    ? mailsService.fetchInboundAttachmentLink(attachment.id)
    : attachment.file;

const downloadFile = async (url, filename) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`No se pudo descargar el adjunto (${response.status}).`);
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

const MAX_URL_REFRESHES = 10;

const createRefreshState = () => ({
  failedUrls: new Set(),
  refreshCount: 0,
  isRefreshing: false,
});

const THUMBNAIL_VARIANT = "thumbnail";

const useRefreshableUrl = (attachment, variant = "") => {
  const initialUrl =
    variant === THUMBNAIL_VARIANT ? attachment.thumbnail : attachment.file;
  const [url, setUrl] = useState(initialUrl);
  const refreshStateRef = useRef(createRefreshState());

  useEffect(() => {
    setUrl(initialUrl);
    refreshStateRef.current = createRefreshState();
  }, [initialUrl]);

  const refreshUrl = useCallback(async () => {
    const refreshState = refreshStateRef.current;
    if (!isInboundAttachment(attachment) || !url) return;
    if (refreshState.isRefreshing || refreshState.failedUrls.has(url)) return;
    if (refreshState.refreshCount >= MAX_URL_REFRESHES) {
      setUrl(null);
      return;
    }

    refreshState.failedUrls.add(url);
    refreshState.refreshCount += 1;
    refreshState.isRefreshing = true;
    try {
      setUrl(
        variant
          ? await mailsService.fetchInboundAttachmentLink(attachment.id, {
              variant,
            })
          : await mailsService.fetchInboundAttachmentLink(attachment.id),
      );
    } catch {
      setUrl(null);
    } finally {
      refreshState.isRefreshing = false;
    }
  }, [attachment, url, variant]);

  return [url, refreshUrl];
};

const MediaAttachment = ({ attachment, kind }) => {
  const [url, refreshUrl] = useRefreshableUrl(attachment);
  const name = getAttachmentDisplayName(attachment);

  if (!url) {
    return <UnavailableAttachment attachment={attachment} />;
  }

  const MediaTag = kind === "video" ? "video" : "audio";

  return (
    <figure className={styles.mediaItem}>
      <MediaTag
        className={kind === "video" ? styles.video : styles.audio}
        src={url}
        controls
        preload="metadata"
        onError={refreshUrl}
        aria-label={name}
      />
      <figcaption className={styles.mediaCaption}>
        <span className={styles.fileName}>{name}</span>
        {getSizeLabel(attachment) && (
          <span className={styles.fileSize}>{getSizeLabel(attachment)}</span>
        )}
      </figcaption>
    </figure>
  );
};

const ImageThumbnail = ({ attachment, onOpen }) => {
  const [url, refreshUrl] = useRefreshableUrl(
    attachment,
    attachment.thumbnail ? THUMBNAIL_VARIANT : "",
  );
  const name = getAttachmentDisplayName(attachment);

  if (!url) {
    return <UnavailableAttachment attachment={attachment} />;
  }

  return (
    <button
      type="button"
      className={styles.thumbnail}
      onClick={() => onOpen(attachment)}
      aria-label={`Ver imagen ${name}`}
      title={name}
    >
      <img src={url} alt={name} loading="lazy" onError={refreshUrl} />
    </button>
  );
};

const FileChip = ({ attachment, kind }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const name = getAttachmentDisplayName(attachment);
  const sizeLabel = getSizeLabel(attachment);
  const label = (
    <>
      <span aria-hidden="true">{kind === "pdf" ? "📄" : "📎"}</span>
      <span className={styles.fileName}>{name}</span>
      {sizeLabel && <span className={styles.fileSize}>({sizeLabel})</span>}
    </>
  );

  if (!isInboundAttachment(attachment)) {
    return (
      <a
        href={attachment.file}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.fileChip}
      >
        {label}
      </a>
    );
  }

  const handleOpen = async () => {
    const previewWindow = kind === "pdf" ? window.open("", "_blank") : null;
    if (previewWindow) previewWindow.opener = null;
    setIsOpening(true);
    setHasFailed(false);
    try {
      const freshUrl = await fetchFreshUrl(attachment);
      if (previewWindow) {
        previewWindow.location.href = freshUrl;
      } else {
        await downloadFile(freshUrl, name);
      }
    } catch {
      previewWindow?.close();
      setHasFailed(true);
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <button
      type="button"
      className={styles.fileChip}
      onClick={handleOpen}
      disabled={isOpening}
      aria-label={`${kind === "pdf" ? "Abrir" : "Descargar"} ${name}`}
    >
      {label}
      {hasFailed && (
        <span className={styles.unavailableReason} role="alert">
          No se pudo descargar, reintentá
        </span>
      )}
    </button>
  );
};

const UnavailableAttachment = ({ attachment }) => {
  const reason =
    UNAVAILABLE_REASON_LABELS[attachment.unavailable_reason] ||
    "No disponible";
  const sizeLabel = getSizeLabel(attachment);

  return (
    <span
      className={`${styles.fileChip} ${styles.fileChipUnavailable}`}
      title={reason}
    >
      <span aria-hidden="true">⚠️</span>
      <span className={styles.fileName}>
        {getAttachmentDisplayName(attachment)}
      </span>
      {sizeLabel && <span className={styles.fileSize}>({sizeLabel})</span>}
      <span className={styles.unavailableReason}>{reason}</span>
    </span>
  );
};

export const ImageViewer = ({ attachment, onClose }) => {
  const [currentUrl, refreshUrl] = useRefreshableUrl(attachment);
  const name = getAttachmentDisplayName(attachment);
  const contentRef = useRef(null);
  const closeButtonRef = useRef(null);

  useModalFocusTrap(contentRef, closeButtonRef, onClose);
  const [hasDownloadFailed, setHasDownloadFailed] = useState(false);

  const handleDownload = async () => {
    setHasDownloadFailed(false);
    try {
      await downloadFile(await fetchFreshUrl(attachment), name);
    } catch {
      try {
        if (!currentUrl) throw new Error("El adjunto no tiene una URL vigente.");
        await downloadFile(currentUrl, name);
      } catch {
        setHasDownloadFailed(true);
      }
    }
  };

  return (
    <ModalOverlay className={styles.viewerBackdrop} onClose={onClose}>
      <div
        ref={contentRef}
        className={styles.viewerContent}
        role="dialog"
        aria-modal="true"
        aria-label={name}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.viewerHeader}>
          <span className={styles.fileName}>{name}</span>
          <div className={styles.viewerActions}>
            {hasDownloadFailed && (
              <span className={styles.unavailableReason} role="alert">
                No se pudo descargar, reintentá
              </span>
            )}
            <button
              type="button"
              className={styles.viewerButton}
              onClick={handleDownload}
            >
              Descargar
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              className={styles.viewerButton}
              onClick={onClose}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
        {currentUrl ? (
          <img
            className={styles.viewerImage}
            src={currentUrl}
            alt={name}
            onError={refreshUrl}
          />
        ) : (
          <UnavailableAttachment attachment={attachment} />
        )}
      </div>
    </ModalOverlay>
  );
};

/**
 * Lista de adjuntos de un correo: miniaturas de imágenes con visor,
 * reproductores de video y audio, y chips para PDF y demás archivos.
 *
 * Los adjuntos que ya se muestran dentro del cuerpo (imágenes inline) no se
 * repiten. Los adjuntos de correos recibidos piden una URL firmada nueva al
 * abrirlos o si la vigente venció.
 */
const AttachmentList = ({ attachments = [], className = "" }) => {
  const [viewerState, setViewerState] = useState(null);
  const closeViewer = useCallback(() => setViewerState(null), []);

  const visibleAttachments = attachments.filter(
    (attachment) => !attachment.embedded_in_body,
  );

  if (!visibleAttachments.length) return null;

  const groupedAttachments = { image: [], media: [], file: [] };
  visibleAttachments.forEach((attachment, index) => {
    const kind = getAttachmentKind(attachment);
    const entry = { attachment, kind, key: getAttachmentKey(attachment, index) };

    if (!isAttachmentAvailable(attachment)) {
      groupedAttachments.file.push({ ...entry, kind: "unavailable" });
    } else if (kind === "image") {
      groupedAttachments.image.push(entry);
    } else if (kind === "video" || kind === "audio") {
      groupedAttachments.media.push(entry);
    } else {
      groupedAttachments.file.push(entry);
    }
  });

  return (
    <div
      className={`${styles.attachmentList} ${className}`}
      aria-label="Adjuntos"
    >
      {groupedAttachments.image.length > 0 && (
        <div className={styles.thumbnailGrid}>
          {groupedAttachments.image.map(({ attachment, key }) => (
            <ImageThumbnail
              key={key}
              attachment={attachment}
              onOpen={(openedAttachment) =>
                setViewerState({ attachment: openedAttachment })
              }
            />
          ))}
        </div>
      )}

      {groupedAttachments.media.map(({ attachment, kind, key }) => (
        <MediaAttachment key={key} attachment={attachment} kind={kind} />
      ))}

      {groupedAttachments.file.length > 0 && (
        <div className={styles.fileChips}>
          {groupedAttachments.file.map(({ attachment, kind, key }) =>
            kind === "unavailable" ? (
              <UnavailableAttachment key={key} attachment={attachment} />
            ) : (
              <FileChip key={key} attachment={attachment} kind={kind} />
            ),
          )}
        </div>
      )}

      {viewerState && (
        <ImageViewer
          attachment={viewerState.attachment}
          onClose={closeViewer}
        />
      )}
    </div>
  );
};

export default AttachmentList;
